"use client";

import NextLink from "next/link";
import { useRouter } from "next/navigation";
import { ComponentProps, MouseEvent } from "react";
import { scrollToElement } from "@/lib/scroll";

const AnchorLink = ({ href, target, onClick, ...props }: ComponentProps<typeof NextLink>) => {
	const router = useRouter();

	const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
		onClick?.(event);

		if (event.metaKey || event.ctrlKey || event.shiftKey || (target && target !== "_self")) return;

		const hrefString = href.toString();
		if (!hrefString.startsWith("/") && !hrefString.startsWith("#")) return;

		const [path, anchorId] = hrefString.split("#");
		if (!anchorId) return;

		event.preventDefault();

		if (document.getElementById(anchorId)) {
			scrollToElement(`#${anchorId}`);
			return;
		}

		if (!path) return;

		router.push(path);

		// The target block only exists after the new page has rendered
		const deadline = Date.now() + 3000;
		const scrollWhenRendered = () => {
			if (document.getElementById(anchorId)) scrollToElement(`#${anchorId}`);
			else if (Date.now() < deadline) requestAnimationFrame(scrollWhenRendered);
		};
		requestAnimationFrame(scrollWhenRendered);
	};

	return <NextLink {...props} href={href} target={target} onClick={handleClick} />;
};

export default AnchorLink;
